import React, { useState, useEffect } from "react";
import MasterLayout from "../Sections/MasterLayout";
import {
  get_blog_category,
  add_blog_category,
  delete_blog_category,
  server_post_data,
} from "../../ServiceConnection/serviceconnection";
import { Edit, Trash2 } from "lucide-react";
import DeleteModal from "../Sections/DeleteModal";

export default function CategoryBlog() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [slug, setSlug] = useState("");
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");

  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // SLUGIFY
  const createSlug = (str) => str.toLowerCase().trim().replace(/\s+/g, "-");

  // Load Data
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_blog_category, fd);
      setCategories(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching:", error);
    }
  };

  // -------------------------------------------
  // ADD CATEGORY
  // -------------------------------------------
  const handleAddCategory = async () => {
    try {
      const fd = new FormData();
      fd.append("name", newCategory);

      const res = await server_post_data(add_blog_category, fd);

      if (res.data.success) {
        fetchCategories();
        setModalOpen(false);
        setNewCategory("");
        setSlug("");
      }
    } catch (error) {
      console.error("ADD ERROR:", error);
    }
  };

  // -------------------------------------------
  // EDIT CATEGORY
  // -------------------------------------------
  const handleEditCategory = async () => {
    try {
      const fd = new FormData();
      fd.append("primary_id", editId);
      fd.append("name", newCategory);

      const res = await server_post_data(add_blog_category, fd);

      if (res.data.success) {
        fetchCategories();
        setModalOpen(false);
        setNewCategory("");
        setSlug("");
        setEditId(null);
      }
    } catch (error) {
      console.error("UPDATE ERROR:", error);
    }
  };

  // -------------------------------------------
  // FINAL SAVE LOGIC
  // -------------------------------------------
  const handleSave = () => {
    if (!newCategory.trim()) {
      setError("Category name is required.");
      return;
    }
    if (editId) {
      handleEditCategory();
    } else {
      handleAddCategory();
    }
  };

  // -------------------------------------------
  // DELETE CATEGORY
  // -------------------------------------------
  const handleDelete = async () => {
    try {
      const fd = new FormData();
      fd.append("primary_id", deleteId);

      await server_post_data(delete_blog_category, fd);

      setDeleteUserModal(false);
      fetchCategories();
    } catch (error) {
      console.error("DELETE ERROR:", error);
    }
  };

  // Search
  const filteredList = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (modalOpen && e.key === "Enter") {
        handleSave();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, newCategory, editId]);
  return (
    <MasterLayout>
      <div className="bg-white p-6 rounded-lg shadow border">
        {/* HEADER */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Blog Categories
              </h2>
              <p className="text-gray-500 text-sm">
                Manage your blog categories
              </p>
            </div>

            <div className="flex gap-3 w-full sm:w-auto">
              {/* Search */}
              <input
                type="text"
                placeholder="🔍 Search..."
                className="border rounded-lg px-4 py-3 w-full sm:w-80 focus:ring-2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {/* Add Button */}
              <button
                onClick={() => {
                  setModalOpen(true);
                  setEditId(null);
                  setNewCategory("");
                  setSlug("");
                }}
                className="px-6 py-3 bg-[var(--primary-color)] text-white rounded-lg"
              >
                + Add Category
              </button>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-left">
            <thead className="border-b bg-gray-50 text-sm font-semibold">
              <tr>
                <th className="p-4">Name</th>
                <th className="p-4">Slug</th>
                <th className="p-4">Blogs</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredList.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-4 text-center text-gray-500">
                    No categories found
                  </td>
                </tr>
              ) : (
                filteredList.map((cat) => (
                  <tr
                    key={cat.primary_id}
                    className="border-b hover:bg-gray-50"
                  >
                    <td className="p-4">{cat.name}</td>
                    <td className="p-4 text-gray-500 text-sm">{cat.slug}</td>

                    <td className="p-4">
                      <span className="px-3 py-1 bg-gray-200 rounded-full">
                        {cat.blogCount || 0}
                      </span>
                    </td>

                    <td className="p-4 text-right space-x-4">
                      {/* EDIT */}
                      <button
                        onClick={() => {
                          setModalOpen(true);
                          setEditId(cat.primary_id);
                          setNewCategory(cat.name);
                          setSlug(cat.slug);
                        }}
                        className="p-2 bg-blue-50 text-blue-600 rounded-lg"
                      >
                        <Edit size={18} />
                      </button>

                      {/* DELETE */}
                      <button
                        onClick={() => {
                          setDeleteId(cat.primary_id);
                          setDeleteUserModal(true);
                        }}
                        className="text-red-600"
                      >
                        <Trash2 size={20} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1000]">
          <div className="bg-white w-96 p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4">
              {editId ? "Edit Category" : "Add Category"}
            </h3>
            {error && (
              <span className="text-red-500 text-sm mb-2 block">{error}</span>
            )}
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Name
            </label>
            {/* Name Input */}
            <input
              type="text"
              className="w-full border p-3 rounded-lg mb-3"
              value={newCategory}
              onChange={(e) => {
                setNewCategory(e.target.value);
                setSlug(createSlug(e.target.value));
                setError("");
              }}
              placeholder="Enter category name"
            />
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Slug
            </label>
            {/* Slug Preview */}
            <input
              type="text"
              disabled
              className="w-full border p-3 rounded-lg bg-gray-100 text-gray-600"
              value={slug}
            />

            <div className="mt-5 flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-200 rounded"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                className="px-6 py-2 bg-[var(--primary-color)] text-white rounded"
              >
                {editId ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      <DeleteModal
        open={deleteUserModal}
        onClose={() => setDeleteUserModal(false)}
        onConfirm={handleDelete}
        title="Delete Category?"
        subtitle="This category will be permanently removed."
      />
    </MasterLayout>
  );
}
